import { useMeasurementPresentation } from '../../Measurement'
import { Dimensions } from '../../Measurement'
import { MeasurementInput } from '../../MeasurementInput'
import { optionalVehicleNumber } from '../../../lib/vehicleFormContract'
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Vehicle } from '../../../types/optimization'
import { AlertOctagon, Plus, Trash2, X, AlertTriangle } from 'lucide-react'

interface Obstacle {
  id: string
  name: string
  position: {
    x: number // mm
    y: number // mm
    z: number // mm
  }
  dimensions: {
    length: number // mm
    width: number // mm
    height: number // mm
  }
}

interface ObstacleEditorProps {
  vehicle: Vehicle | null | undefined
  onChange: (updatedVehicle: Vehicle) => void
  mode?: 'edit' | 'view'
  isTractor?: boolean
  selectedObstacleId?: string | null
  onSelectObstacle?: (id: string | null) => void
}

const inputCls = "w-full px-2.5 py-1.5 text-xs rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"

export const ObstacleEditor: React.FC<ObstacleEditorProps> = ({
  vehicle,
  onChange,
  mode = 'edit',
  isTractor = false,
  selectedObstacleId,
  onSelectObstacle,
}) => {
  const { format: formatMeasure } = useMeasurementPresentation()

  // The parent form is the source of truth when a profile changes in another panel.
  const obstacles = (vehicle?.obstacles || []) as Obstacle[]
  const vehicleLength = optionalVehicleNumber(vehicle?.dimensions?.length ?? vehicle?.length) ?? 0
  const vehicleWidth = optionalVehicleNumber(vehicle?.dimensions?.width ?? vehicle?.width) ?? 0
  const vehicleHeight = optionalVehicleNumber(vehicle?.dimensions?.height ?? vehicle?.height) ?? 0
  const [localEditingId, setLocalEditingId] = useState<string | null>(null)
  const editingId = selectedObstacleId === undefined ? localEditingId : selectedObstacleId
  const setEditingId = onSelectObstacle ?? setLocalEditingId
  const layoutRef = useRef<HTMLDivElement>(null)
  const [layoutWidth, setLayoutWidth] = useState(0)
  useEffect(() => {
    const element = layoutRef.current
    if (!element) return
    const observer = new ResizeObserver(() => setLayoutWidth(element.clientWidth))
    observer.observe(element)
    return () => observer.disconnect()
  }, [vehicleLength, vehicleWidth, vehicleHeight, isTractor])
  const nameRef = useRef<HTMLInputElement>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef(new Map<string, HTMLButtonElement>())
  const editingObstacle = obstacles.find(obstacle => obstacle.id === editingId) ?? null

  const handleAddObstacle = useCallback(() => {
    if (mode === 'view' || !vehicle) return

    const defaultLength = Math.min(1000, vehicleLength)
    const defaultWidth = Math.min(1000, vehicleWidth)
    const defaultHeight = Math.min(1000, vehicleHeight)

    const defaultX = Math.min(Math.round(vehicleLength / 3), vehicleLength - defaultLength)
    const defaultY = Math.min(Math.round(vehicleWidth / 2), vehicleWidth - defaultWidth)
    const defaultZ = 0

    const newObstacle: Obstacle = {
      id: `obstacle-${Date.now()}`,
      name: `Obstacle ${obstacles.length + 1}`,
      position: {
        x: defaultX,
        y: defaultY,
        z: defaultZ
      },
      dimensions: {
        length: defaultLength,
        width: defaultWidth,
        height: defaultHeight
      }
    }

    const updatedObstacles = [...obstacles, newObstacle]
    setEditingId(newObstacle.id)
    requestAnimationFrame(() => nameRef.current?.focus())

    onChange({
      ...vehicle,
      obstacles: updatedObstacles
    })
  }, [vehicle, onChange, obstacles, vehicleLength, vehicleWidth, vehicleHeight, mode])

  const handleUpdateObstacle = useCallback((obstacleId: string, updatedObstacle: Partial<Obstacle>) => {
    if (mode === 'view' || !vehicle) return

    const currentObstacle = obstacles.find(o => o.id === obstacleId)
    if (!currentObstacle) return

    let validatedLength = updatedObstacle.dimensions?.length ?? currentObstacle.dimensions.length
    let validatedWidth = updatedObstacle.dimensions?.width ?? currentObstacle.dimensions.width
    let validatedHeight = updatedObstacle.dimensions?.height ?? currentObstacle.dimensions.height

    validatedLength = Math.max(0, validatedLength)
    validatedWidth = Math.max(0, validatedWidth)
    validatedHeight = Math.max(0, validatedHeight)

    validatedLength = Math.min(validatedLength, vehicleLength)
    validatedWidth = Math.min(validatedWidth, vehicleWidth)
    validatedHeight = Math.min(validatedHeight, vehicleHeight)

    let validatedX = updatedObstacle.position?.x ?? currentObstacle.position.x
    let validatedY = updatedObstacle.position?.y ?? currentObstacle.position.y
    let validatedZ = updatedObstacle.position?.z ?? currentObstacle.position.z

    validatedX = Math.max(0, validatedX)
    validatedY = Math.max(0, validatedY)
    validatedZ = Math.max(0, validatedZ)

    if (validatedX + validatedLength > vehicleLength) {
      validatedX = Math.max(0, vehicleLength - validatedLength)
    }

    if (validatedY + validatedWidth > vehicleWidth) {
      validatedY = Math.max(0, vehicleWidth - validatedWidth)
    }

    if (validatedZ + validatedHeight > vehicleHeight) {
      validatedZ = Math.max(0, vehicleHeight - validatedHeight)
    }

    const finalUpdatedObstacle: Obstacle = {
      ...currentObstacle,
      ...updatedObstacle,
      position: {
        x: validatedX,
        y: validatedY,
        z: validatedZ
      },
      dimensions: {
        length: validatedLength,
        width: validatedWidth,
        height: validatedHeight
      }
    }

    const updatedObstacles = obstacles.map(obstacle =>
      obstacle.id === obstacleId ? finalUpdatedObstacle : obstacle
    )

    onChange({
      ...vehicle,
      obstacles: updatedObstacles
    })
  }, [vehicle, onChange, obstacles, editingObstacle, mode, vehicleLength, vehicleWidth, vehicleHeight])

  const handleRemoveObstacle = useCallback((obstacleId: string) => {
    if (mode === 'view' || !vehicle) return

    const updatedObstacles = obstacles.filter(obstacle => obstacle.id !== obstacleId)
    const nextFocus = updatedObstacles[Math.min(obstacles.findIndex(obstacle => obstacle.id === obstacleId), updatedObstacles.length - 1)]?.id
    if (editingObstacle?.id === obstacleId) {
      setEditingId(null)
    }

    onChange({
      ...vehicle,
      obstacles: updatedObstacles
    })
    requestAnimationFrame(() => (nextFocus ? itemRefs.current.get(nextFocus) : addRef.current)?.focus())
  }, [vehicle, onChange, obstacles, editingObstacle, mode])

  if (!isTractor && (vehicleLength <= 0 || vehicleWidth <= 0 || vehicleHeight <= 0)) {
    return <p role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">Renseignez les dimensions intérieures du véhicule pour configurer les obstacles. Les obstacles existants sont conservés.</p>
  }

  const wide = layoutWidth >= 900
  const columns = layoutWidth >= 620
  const axes = [
    { axis: 'x' as const, dimension: 'length' as const, label: 'Longueur', id: 'len', extent: vehicleLength, origin: 'Depuis l’avant' },
    { axis: 'y' as const, dimension: 'width' as const, label: 'Largeur', id: 'width', extent: vehicleWidth, origin: 'Depuis le côté gauche' },
    { axis: 'z' as const, dimension: 'height' as const, label: 'Hauteur', id: 'height', extent: vehicleHeight, origin: 'Depuis le plancher' },
  ]
  const measureCls = 'w-full min-w-0 rounded-md border border-transparent bg-white px-1.5 py-0.5 text-right text-xs font-semibold tabular-nums text-slate-900 outline-none hover:border-orange-200 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 dark:bg-slate-950 dark:text-slate-100'
  return <div ref={layoutRef} data-obstacle-editor className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
    <div className="flex min-h-7 flex-wrap items-center justify-between gap-2">
      <p className="flex items-center gap-1.5 text-xs text-slate-500"><AlertOctagon aria-hidden="true" size={14} className="text-orange-500" />{obstacles.length} obstacle(s) · zones non chargeables</p>
      {!isTractor && mode === 'edit' && <button ref={addRef} type="button" onClick={handleAddObstacle} className="inline-flex min-h-7 items-center gap-1 rounded-md border border-orange-200 px-2 text-xs text-orange-700 outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:border-orange-800 dark:text-orange-300"><Plus aria-hidden="true" size={13} />Ajouter un obstacle</button>}
    </div>
    {isTractor ? <p className="flex gap-2 rounded-xl border p-3 text-xs text-slate-500"><AlertTriangle aria-hidden="true" size={16} />La gestion des obstacles n'est pas applicable aux tracteurs.</p> : <div data-obstacle-workspace className="grid min-h-0 min-w-0 flex-1 items-stretch gap-2"
      style={{ gridTemplateColumns: wide ? 'minmax(0, 1fr) minmax(0, .9fr) minmax(0, 2fr)' : columns ? 'minmax(160px, .8fr) minmax(0, 1.8fr)' : 'minmax(0, 1fr)' }}>
      {wide && <section data-obstacle-plan className="min-w-0 rounded-xl border border-slate-200 p-2 dark:border-slate-700">
        <h4 className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Repérage · vue de dessus</h4>
        <svg viewBox="0 0 400 160" className="mt-1 w-full" role="img" aria-label="Plan des obstacles dans l’espace de chargement">
          <rect x="16" y="28" width="368" height="112" rx="3" className="fill-slate-50 stroke-slate-400 dark:fill-slate-900" />
          <path d="M16 17H384M377 13L384 17L377 21" fill="none" className="stroke-slate-400" />
          <text x="16" y="10" fontSize="12" className="fill-slate-500">Avant</text><text x="384" y="10" textAnchor="end" fontSize="12" className="fill-slate-500">Arrière · X</text>
          {obstacles.map(obstacle => <rect key={obstacle.id} data-obstacle-footprint={obstacle.id} x={16 + obstacle.position.x / vehicleLength * 368} y={28 + obstacle.position.y / vehicleWidth * 112}
            width={Math.max(1, obstacle.dimensions.length / vehicleLength * 368)} height={Math.max(1, obstacle.dimensions.width / vehicleWidth * 112)} rx="1" fill={editingId === obstacle.id ? '#f97316' : '#3b82f6'} fillOpacity=".3" stroke={editingId === obstacle.id ? '#ea580c' : '#2563eb'}>
            <title>{obstacle.name}</title>
          </rect>)}
        </svg>
        <p className="text-[10px] leading-4 text-slate-500"><Dimensions length={vehicleLength} width={vehicleWidth} height={vehicleHeight} from="mm" quantity="vehicleLength" /></p>
        <p className="mt-1 text-[10px] leading-4 text-slate-400">X : avant → arrière · Y : gauche → droite · Z : hauteur.</p>
      </section>}
      <section data-obstacle-list aria-label="Obstacles configurés" className={'min-w-0 rounded-xl border border-slate-200 p-2 dark:border-slate-700 ' + (columns ? 'min-h-0 overflow-y-auto' : '')}>
        {!obstacles.length ? <p className="py-4 text-center text-xs text-slate-500">Aucun obstacle défini pour ce véhicule.</p> : <div className="space-y-1.5">
          {obstacles.map(obstacle => <div key={obstacle.id} className={'flex min-w-0 items-center rounded-lg border ' + (editingId === obstacle.id ? 'border-orange-300 bg-orange-50/60 dark:border-orange-800 dark:bg-orange-950/30' : 'border-slate-200 dark:border-slate-700')}>
            <button type="button" ref={element => { if (element) itemRefs.current.set(obstacle.id, element); else itemRefs.current.delete(obstacle.id) }} aria-pressed={editingId === obstacle.id}
              onClick={() => { setEditingId(obstacle.id); requestAnimationFrame(() => nameRef.current?.focus()) }} className="min-w-0 flex-1 rounded-lg p-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-orange-500">
              <span className="block truncate text-xs font-semibold text-slate-800 dark:text-slate-100">{obstacle.name}</span>
              <span className="block text-[10px] text-slate-500"><Dimensions {...obstacle.dimensions} quantity="vehicleLength" /></span>
              <span className="block text-[9px] text-slate-400">{axes.map(entry => entry.axis.toUpperCase() + ' ' + formatMeasure(obstacle.position[entry.axis], 'mm', 'vehicleLength')).join(' · ')}</span>
            </button>
            {mode === 'edit' && <button type="button" aria-label={'Supprimer l’obstacle ' + obstacle.name} onClick={() => handleRemoveObstacle(obstacle.id)} className="shrink-0 rounded p-1.5 text-red-500 outline-none hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-orange-500 dark:hover:bg-red-950"><Trash2 aria-hidden="true" size={13} /></button>}
          </div>)}
        </div>}
      </section>
      {editingObstacle ? <section data-obstacle-details className="min-w-0 self-start rounded-xl border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor="obstacle-name-input" className="sr-only">Nom de l’obstacle</label>
          <input ref={nameRef} id="obstacle-name-input" type="text" value={editingObstacle.name} onChange={event => handleUpdateObstacle(editingObstacle.id, { name: event.target.value })} disabled={mode === 'view'} className={inputCls + ' min-w-0 flex-1 !py-1 font-semibold'} />
          <button type="button" aria-label="Fermer l’édition de l’obstacle" onClick={() => { setEditingId(null); requestAnimationFrame(() => itemRefs.current.get(editingObstacle.id)?.focus()) }} className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 outline-none focus-visible:ring-2 focus-visible:ring-orange-500"><X aria-hidden="true" size={14} /></button>
        </div>
        <fieldset className="min-w-0"><legend className="mb-0.5 text-[10px] font-semibold text-slate-500">Position · depuis l’avant, la gauche et le plancher</legend>
          <div className="grid min-w-0 grid-cols-3 gap-2">{axes.map(entry => <div key={entry.axis} className="min-w-0">
            <label htmlFor={'obstacle-pos-' + entry.axis + '-value'} title={entry.origin} className="block text-[10px] text-slate-500">{entry.axis.toUpperCase()}</label>
            <MeasurementInput compact label={'Position ' + entry.axis.toUpperCase()} quantity="vehicleLength" storageUnit="mm" value={editingObstacle.position[entry.axis]} id={'obstacle-pos-' + entry.axis + '-value'} disabled={mode === 'view'} className={measureCls} min={0} max={Math.max(0, entry.extent - editingObstacle.dimensions[entry.dimension])}
              onValueChange={value => handleUpdateObstacle(editingObstacle.id, { position: { ...editingObstacle.position, [entry.axis]: value ?? 0 } })} />
            <input id={'obstacle-pos-' + entry.axis + '-input'} type="range" aria-label={'Déplacer l’obstacle selon ' + entry.axis.toUpperCase()} aria-valuetext={formatMeasure(editingObstacle.position[entry.axis], 'mm', 'vehicleLength')} min={0} max={Math.max(0, entry.extent - editingObstacle.dimensions[entry.dimension])} value={editingObstacle.position[entry.axis]} disabled={mode === 'view'}
              onChange={event => handleUpdateObstacle(editingObstacle.id, { position: { ...editingObstacle.position, [entry.axis]: Number(event.target.value) } })} className="block h-3 w-full accent-orange-500" />
          </div>)}</div>
        </fieldset>
        <fieldset className="mt-1 min-w-0"><legend className="mb-0.5 text-[10px] font-semibold text-slate-500">Dimensions</legend>
          <div className="grid min-w-0 grid-cols-3 gap-2">{axes.map(entry => <div key={entry.dimension} className="min-w-0">
            <label htmlFor={'obstacle-' + entry.id + '-input'} className="block text-[10px] text-slate-500">{entry.label}</label>
            <MeasurementInput compact label={entry.label} quantity="vehicleLength" storageUnit="mm" value={editingObstacle.dimensions[entry.dimension]} id={'obstacle-' + entry.id + '-input'} disabled={mode === 'view'} className={measureCls} min={0} max={entry.extent}
              onValueChange={value => handleUpdateObstacle(editingObstacle.id, { dimensions: { ...editingObstacle.dimensions, [entry.dimension]: value ?? 0 } })} />
          </div>)}</div>
        </fieldset>
      </section> : <p className="self-start rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-500 dark:border-slate-700">{obstacles.length ? 'Sélectionnez un obstacle pour consulter sa position et ses dimensions.' : 'Ajoutez un obstacle pour définir sa position et ses dimensions.'}</p>}
    </div>}
  </div>
}
